import { useState, useRef } from "react";
import { api, formatErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Loader2, X, Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  customer_name: "", customer_account_id: "", customer_phone: "",
  complaint_type: "", priority: "Medium", location: "", address: "",
  description: "",
};

export default function RaiseComplaintDialog({ open, onOpenChange, onCreated }) {
  const { config } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const token = () => localStorage.getItem("np_token") || "";

  async function handleFiles(files) {
    if (!files?.length) return;
    if (photos.length + files.length > 6) { toast.error("Max 6 photos"); return; }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data]);
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  function removePhoto(id) { setPhotos((p) => p.filter((x) => x.id !== id)); }

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error("Customer name required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", { ...form, photo_ids: photos.map((p) => p.id) });
      toast.success(`Ticket ${data.ticket_no} raised`);
      setForm(EMPTY); setPhotos([]);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-white">Raise complaint on behalf of a customer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Customer name *</Label>
              <Input data-testid="rc-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Full name" className="bg-slate-900 border-slate-700 mt-1" required />
            </div>
            <div>
              <Label className="text-slate-300">Customer ID</Label>
              <Input data-testid="rc-account-id" value={form.customer_account_id} onChange={(e) => setForm({ ...form, customer_account_id: e.target.value })} placeholder="NVS account / subscriber ID" className="bg-slate-900 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Mobile number</Label>
              <Input data-testid="rc-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+91…" className="bg-slate-900 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Location</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="rc-location" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {config.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Complaint type</Label>
              <Select value={form.complaint_type} onValueChange={(v) => setForm({ ...form, complaint_type: v })}>
                <SelectTrigger data-testid="rc-type" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {config.complaint_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="rc-priority" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {config.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Address / landmark</Label>
            <Input data-testid="rc-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House / street / area" className="bg-slate-900 border-slate-700 mt-1" required />
          </div>
          <div>
            <Label className="text-slate-300">Describe the issue</Label>
            <Textarea data-testid="rc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Loss light on ONT, no internet since 10 AM" rows={3} className="bg-slate-900 border-slate-700 mt-1" required />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Photos {uploading && <Loader2 className="w-3.5 h-3.5 inline animate-spin ml-1 text-purple-400" />}</Label>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(Array.from(e.target.files || []))} capture="environment" />
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-slate-700 group">
                  <img alt="" src={`${API}/files/${p.id}?auth=${token()}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-0 right-0 bg-black/70 text-white p-0.5 rounded-bl hover:bg-rose-500"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {photos.length < 6 && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-16 h-16 rounded-md border border-dashed border-slate-700 flex items-center justify-center text-slate-500 hover:border-purple-500/60 hover:text-purple-300" data-testid="rc-add-photo">
                  <Camera className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="text-[11px] text-slate-500">Optional · up to 6 photos of the router, wiring or ONT.</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-200 hover:bg-slate-800">Cancel</Button>
            <Button data-testid="rc-submit" type="submit" disabled={submitting || uploading} className="bg-purple-500 hover:bg-purple-600">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} <Plus className="w-4 h-4 mr-1" /> Raise complaint
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
